import {Injectable} from '@angular/core';
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {
    ABTestActionResponse,
    ABTestResultsComparison,
    AddTestGroupRequest,
    ApplyWinnerResponse,
    CreateABTestRequest,
    SelectWinnerRequest
} from "../../model/dto/api/ab-test.interface";
import {ABTestGroupDto} from "../../model/dto/entity/ab-test-group.dto";
import {CampaignABTestDto} from "../../model/dto/entity/campaign-ab-test.dto";

/**
 * Service for managing Campaign A/B Tests (F-026).
 * Provides operations for configuring, running, and analyzing A/B tests.
 */
@Injectable({
    providedIn: 'root'
})
export class CampaignABTestService extends MvsCrudService {

    constructor(protected http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ct/campaignABTests')
    }

    getObjectIcon(): string {
        return 'fa-solid fa-flask';
    }

    /**
     * Creates a new A/B test for a campaign.
     * F-026: Configure A/B test for comparing different approaches.
     *
     * @param request the create request
     */
    createABTest(request: CreateABTestRequest): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>(request, '/create');
    }

    /**
     * Adds a test group to an A/B test.
     * F-026: Configure control and variant groups with allocation percentages.
     *
     * @param abTestId the A/B test ID
     * @param request the group configuration
     */
    addTestGroup(abTestId: number, request: AddTestGroupRequest): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>(request, `/${abTestId}/groups`);
    }

    /**
     * Gets all groups for an A/B test.
     *
     * @param abTestId the A/B test ID
     */
    getTestGroups(abTestId: number): Observable<ABTestGroupDto[]> {
        return this.getGeneric<ABTestGroupDto[]>(`/${abTestId}/groups`);
    }

    /**
     * Starts an A/B test by randomly splitting campaign members into groups.
     * F-026: Given a campaign, when A/B test is configured, then members are randomly split into groups.
     *
     * @param abTestId the A/B test ID
     */
    startABTest(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/start`);
    }

    /**
     * Pauses a running A/B test.
     *
     * @param abTestId the A/B test ID
     */
    pauseABTest(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/pause`);
    }

    /**
     * Resumes a paused A/B test.
     *
     * @param abTestId the A/B test ID
     */
    resumeABTest(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/resume`);
    }

    /**
     * Ends the test for decision making.
     * F-026: Given test groups exist, when processing completes, then outcomes can be compared statistically.
     *
     * @param abTestId the A/B test ID
     */
    endTestForDecision(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/end-for-decision`);
    }

    /**
     * Gets the current results comparison for an A/B test.
     * F-026: Provides statistical comparison between test groups.
     *
     * @param abTestId the A/B test ID
     */
    getResults(abTestId: number): Observable<ABTestResultsComparison> {
        return this.getGeneric<ABTestResultsComparison>(`/${abTestId}/results`);
    }

    /**
     * Refreshes the results snapshot for an A/B test.
     *
     * @param abTestId the A/B test ID
     */
    refreshResults(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/refresh-results`);
    }

    /**
     * Selects a winning group for the A/B test.
     * F-026: Given A/B test results, when winner is identified, then remaining members can receive winning approach.
     *
     * @param abTestId the A/B test ID
     * @param winningGroupId the winning group ID
     */
    selectWinner(abTestId: number, winningGroupId: number): Observable<ABTestActionResponse> {
        const request: SelectWinnerRequest = {winningGroupId};
        return this.postGeneric<ABTestActionResponse>(request, `/${abTestId}/select-winner`);
    }

    /**
     * Applies the winning approach to remaining campaign members.
     * F-026: Given A/B test results, when winner is identified, then remaining members can receive winning approach.
     *
     * @param abTestId the A/B test ID
     */
    applyWinnerToRemainingMembers(abTestId: number): Observable<ApplyWinnerResponse> {
        return this.postGeneric<ApplyWinnerResponse>({}, `/${abTestId}/apply-winner`);
    }

    /**
     * Cancels an A/B test.
     *
     * @param abTestId the A/B test ID
     */
    cancelABTest(abTestId: number): Observable<ABTestActionResponse> {
        return this.postGeneric<ABTestActionResponse>({}, `/${abTestId}/cancel`);
    }

    /**
     * Gets all A/B tests for a campaign.
     *
     * @param campaignId the campaign ID
     */
    getTestsForCampaign(campaignId: number): Observable<CampaignABTestDto[]> {
        return this.getGeneric<CampaignABTestDto[]>(`/by-campaign/${campaignId}`);
    }
}
